/* eslint-disable import/no-extraneous-dependencies */
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { status } from 'constants/general';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import Button from 'modules/common/components/Button';
import Popup from 'modules/common/components/Popup';
import ItemWithImageSearchField from 'modules/common/components/ItemWithImageSearchField';
import { fetchDoctorsList, submitProvidersBulk } from 'modules/categories/categoriesSlice';
import { getDoctorProfile } from 'modules/categories/api';
import appModeService from 'services/AppModeService';

import { getDoctorsListForDropDown, buildProvidersBulkPayload } from './helper';
import {
  PopupTitleSpan,
  PopupWrapper,
  PopupTitle,
  PopupTitleWrapper,
  Container,
  PopupTitleContainer,
  extendSearchDropDownStyle,
  extendDropDownListItem,
  extendLoaderStyle,
  ButtonsContainer,
  extendGrayButtonStyle,
  extendBlueButtonStyle,
  ItemsContainer,
  ItemContainer,
  ItemWrapper,
} from './AddDoctorsModal.style';
import DoctorRecord from './DoctorRecord';

const AddDoctorsModal = ({ onClose, priceListId, onAddSuccess }) => {
  const dispatch = useDispatch();
  const doctors = useSelector(state => state.categories.doctors);
  const healthPlanCountryId = useSelector(
    state => state?.healthPlans?.selectedHealthPlan?.data?.countryId,
  );
  const submitProvidersBulkState = useSelector(state => state.categories.submitProvidersBulk);
  const isSubmitting = submitProvidersBulkState.status === status.SUBMITING;
  const [selectedProviders, setSelectedProviders] = useState([]);
  const lastSubmittedPayloadRef = useRef(null);

  const searchDoctors = (keyWord, page) => {
    dispatch(fetchDoctorsList({ BookingTypes: 'physical,telehealth', doctorname: keyWord, page }));
  };

  const onSelectDoctor = async doctorInfo => {
    const { EntityKey } = doctorInfo;
    getDoctorProfile(`entitylistingkey=${EntityKey}`, healthPlanCountryId)
      .then(response => {
        const branches = [];
        response?.data?.Profile?.Contacts?.forEach(contact => {
          const { BranchKey, BranchDisplayName, ExaminationRoomKey, Area, BookingType } = contact;
          const { Entity: entity } = contact;
          const { Key: key } = entity;
          // if (BookingType === 'physical')
          branches.push({
            entityKey: key,
            branchName: BranchDisplayName,
            branchKey: BranchKey,
            isChecked: false,
            roomKey: ExaminationRoomKey,
            areaKey: Area?.Key,
            isExclusive: false,
            isSpoke: false,
          });
        });
        setSelectedProviders([...selectedProviders, { ...doctorInfo, branches }]);
      })
      .catch(() => {
        setSelectedProviders([...selectedProviders, doctorInfo]);
      });
  };

  const removeDoctor = (accountKey, examinationRoomKey) => {
    const filteredDoctors = [];
    selectedProviders?.forEach(doctor => {
      if (
        `${doctor.AccountKey}-${doctor.ExaminationRoomKey}` !==
        `${accountKey}-${examinationRoomKey}`
      ) {
        filteredDoctors.push(doctor);
      }
    });
    setSelectedProviders(filteredDoctors);
  };

  const onChangeBranchSelection = (accountKey, examinationRoomKey, branchKey) => {
    const tempSelectedProviders = _.cloneDeep(selectedProviders);
    const indexOfDoctor = selectedProviders.findIndex(
      dr => `${dr.AccountKey}-${dr.ExaminationRoomKey}` === `${accountKey}-${examinationRoomKey}`,
    );
    const indexOfBranch = tempSelectedProviders[indexOfDoctor]?.branches?.findIndex(
      branch => branch.branchKey === branchKey,
    );
    tempSelectedProviders[indexOfDoctor].branches[indexOfBranch].isChecked =
      !tempSelectedProviders[indexOfDoctor].branches[indexOfBranch].isChecked;
    setSelectedProviders(tempSelectedProviders);
  };

  const onExclusiveChange = (accountKey, examinationRoomKey, branchKey, newExclusiveState) => {
    const tempSelectedProviders = _.cloneDeep(selectedProviders);
    const indexOfDoctor = selectedProviders.findIndex(
      dr => `${dr.AccountKey}-${dr.ExaminationRoomKey}` === `${accountKey}-${examinationRoomKey}`,
    );
    const indexOfBranch = tempSelectedProviders[indexOfDoctor]?.branches?.findIndex(
      branch => branch.branchKey === branchKey,
    );

    // Set the current branch's exclusive status (no mutual exclusivity)
    // Set the current branch's exclusive status (no mutual exclusivity)
    if (appModeService.isEmbedded) {
      tempSelectedProviders[indexOfDoctor].branches[indexOfBranch].isSpoke = newExclusiveState;
    } else {
      tempSelectedProviders[indexOfDoctor].branches[indexOfBranch].isExclusive = newExclusiveState;
    }
    setSelectedProviders(tempSelectedProviders);
  };

  const onSave = () => {
    const payload = buildProvidersBulkPayload(priceListId, selectedProviders);
    lastSubmittedPayloadRef.current = payload;
    dispatch(submitProvidersBulk(payload));
  };

  const onClickClose = () => {
    setSelectedProviders([]);
    onClose();
  };

  const isBranchesSelected = () => {
    let isSeleted = false;
    selectedProviders.forEach(provider => {
      provider.branches.forEach(branch => {
        if (branch.isChecked) isSeleted = true;
      });
    });
    return isSeleted;
  };

  const prevStatusRef = useRef(submitProvidersBulkState.status);
  useEffect(() => {
    const didSucceed =
      submitProvidersBulkState.status === status.SUCCESS &&
      prevStatusRef.current !== status.SUCCESS;

    if (didSucceed && lastSubmittedPayloadRef.current) {
      onAddSuccess?.(lastSubmittedPayloadRef.current);
      lastSubmittedPayloadRef.current = null;
      onClickClose();
    }

    prevStatusRef.current = submitProvidersBulkState.status;
  }, [submitProvidersBulkState.status]);

  return (
    <Popup showCloseBtn={false}>
      <PopupWrapper>
        <PopupTitleContainer>
          <PopupTitleWrapper>
            <PopupTitle>Add doctor</PopupTitle>
            <PopupTitleSpan
              onClick={() => {
                onClickClose();
              }}
            />
          </PopupTitleWrapper>
        </PopupTitleContainer>

        <Container>
          <div
            style={{
              height: '520px',
            }}
          >
            {/** Search */}
            <div
              style={{
                padding: '16px',
                background: '#F1F4F6',
              }}
            >
              <ItemWithImageSearchField
                isRequired
                isDynamic
                isDropDown
                searchable
                key="doctors-search-list"
                componentName="doctors-search-list"
                onTypingDelay={500}
                placeHolder="Search for a doctor"
                fieldValue=""
                options={getDoctorsListForDropDown(doctors?.data, selectedProviders)}
                onChanges={payload => {
                  onSelectDoctor(payload?.extras);
                }}
                onSearch={(keyWord, pageNumber) => {
                  searchDoctors(keyWord, pageNumber);
                }}
                language="en"
                isLoading={doctors.status === status.FETCHING}
                extendLoaderStyle={extendLoaderStyle}
                isValid
                extendDropDownStyle={extendSearchDropDownStyle}
                extendDropDownListItem={extendDropDownListItem}
                icon="e905"
                loadOnScroll
              />
            </div>
            {/** Added Doctors section */}
            <div
              style={{
                height: '375px',
              }}
            >
              {selectedProviders?.length > 0 && (
                <div>
                  <ItemsContainer>
                    <ItemContainer>
                      <ItemWrapper>Doctor</ItemWrapper>
                      <ItemWrapper>Branch</ItemWrapper>
                      <ItemWrapper>{appModeService.isEmbedded ? 'SPOC' : 'Exclusive'}</ItemWrapper>
                    </ItemContainer>
                    {selectedProviders?.map(item => {
                      const {
                        DoctorNameEnglish,
                        ImageUrl,
                        MainSpecialtyNameEnglish,
                        AccountKey,
                        branches,
                        TopLevelEntityName,
                        ExaminationRoomKey,
                      } = item;
                      return (
                        <DoctorRecord
                          key={`${AccountKey}-${ExaminationRoomKey}`}
                          accountKey={AccountKey}
                          examinationRoomKey={ExaminationRoomKey}
                          image={ImageUrl}
                          name={DoctorNameEnglish}
                          specialtyName={MainSpecialtyNameEnglish}
                          branches={branches}
                          topLevelEntityName={TopLevelEntityName}
                          onChangeBranchSelection={onChangeBranchSelection}
                          onExclusiveChange={onExclusiveChange}
                          isEmbedded={appModeService.isEmbedded}
                          removeItem={() => removeDoctor(AccountKey, ExaminationRoomKey)}
                        />
                      );
                    })}
                  </ItemsContainer>
                </div>
              )}
            </div>
            <ButtonsContainer>
              <Button
                onClick={() => {
                  onClickClose();
                }}
                btnText="Cancel"
                extendButtonStyle={extendGrayButtonStyle}
                key="dismiss-popup"
              />
              <Button
                onClick={() => {
                  onSave();
                }}
                btnText="Save"
                extendButtonStyle={extendBlueButtonStyle}
                key="proceed-save-view"
                disabled={!isBranchesSelected()}
                isLoading={isSubmitting}
                extendLoaderStyle={extendLoaderStyle}
              />
            </ButtonsContainer>
          </div>
        </Container>
      </PopupWrapper>
    </Popup>
  );
};

AddDoctorsModal.propTypes = {
  onClose: PropTypes.func,
  priceListId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAddSuccess: PropTypes.func,
};

export default AddDoctorsModal;
